import { parseWidgetFullScreenMessage, WidgetFullScreenMessage } from './WidgetFullScreenProtocol';

export interface WidgetFullScreenState {
    embeddedWidget: boolean;
    supported: boolean;
    fullScreen: boolean;
    pendingRequestId: number | null;
    errorCode: 'unavailable' | 'busy' | 'timeout' | null;
}

export interface WidgetFullScreenBridgeOptions {
    isWidget: boolean;
    dialogLabel: () => string;
}

type BridgeWindow = Window & { __smartGuideWidgetFullScreenBridge?: WidgetFullScreenBridge };

export class WidgetFullScreenBridge {
    private state: WidgetFullScreenState;
    private origin: string | null = null;
    private instanceId: string | null = null;
    private connectionId: string | null = null;
    private nextRequestId = 0;
    private timeout: number | null = null;
    private listeners = new Set<(state: Readonly<WidgetFullScreenState>) => void>();
    private escapeWithOverlay = new WeakSet<Event>();

    constructor(private global: BridgeWindow, private document: Document, private options: WidgetFullScreenBridgeOptions) {
        this.state = { embeddedWidget: options.isWidget && global.parent !== global, supported: false, fullScreen: false, pendingRequestId: null, errorCode: null };
        if (!this.state.embeddedWidget) return;
        global.addEventListener('message', this.receive);
        global.addEventListener('keydown', this.captureEscape, true);
        global.addEventListener('keydown', this.escape);
    }

    getState(): Readonly<WidgetFullScreenState> { return { ...this.state }; }

    subscribe(listener: (state: Readonly<WidgetFullScreenState>) => void): () => void {
        this.listeners.add(listener);
        listener(this.getState());
        return () => this.listeners.delete(listener);
    }

    requestFullScreen(fullScreen: boolean): void {
        if (!this.state.supported || this.state.pendingRequestId !== null || !this.origin || !this.instanceId || !this.connectionId) return;
        const requestId = ++this.nextRequestId;
        this.update({ pendingRequestId: requestId, errorCode: null });
        this.timeout = this.global.setTimeout(() => {
            this.timeout = null;
            this.update({ pendingRequestId: null, errorCode: 'timeout' });
        }, 2000);
        this.global.parent.postMessage({ channel: 'smartguide-widget', version: 1, type: 'set-full-screen', instanceId: this.instanceId, connectionId: this.connectionId, requestId, fullScreen } satisfies WidgetFullScreenMessage, this.origin);
    }

    dispose(): void {
        this.clearPending();
        this.global.removeEventListener('message', this.receive);
        this.global.removeEventListener('keydown', this.captureEscape, true);
        this.global.removeEventListener('keydown', this.escape);
        this.listeners.clear();
        if (this.global.__smartGuideWidgetFullScreenBridge === this) delete this.global.__smartGuideWidgetFullScreenBridge;
    }

    private receive = (event: MessageEvent): void => {
        if (event.source !== this.global.parent || !/^https?:\/\//.test(event.origin)) return;
        const message = parseWidgetFullScreenMessage(event.data);
        if (!message || (this.origin && event.origin !== this.origin)) return;
        if (message.type === 'host-init') {
            if (this.instanceId && message.instanceId !== this.instanceId) return;
            this.origin = event.origin;
            this.instanceId = message.instanceId;
            this.connectionId = message.connectionId;
            this.clearPending();
            this.update({ supported: message.fullScreenSupported, fullScreen: message.fullScreen, pendingRequestId: null, errorCode: null });
            this.global.parent.postMessage({ channel: 'smartguide-widget', version: 1, type: 'app-ready', instanceId: message.instanceId, connectionId: message.connectionId, dialogLabel: this.options.dialogLabel() } satisfies WidgetFullScreenMessage, event.origin);
        } else if (message.type === 'full-screen-state' && message.instanceId === this.instanceId && message.connectionId === this.connectionId) {
            if (message.requestId !== undefined && message.requestId < this.nextRequestId) return;
            this.clearPending();
            const changed = this.state.fullScreen !== message.fullScreen;
            this.update({ fullScreen: message.fullScreen, pendingRequestId: null, errorCode: message.errorCode ?? null });
            if (changed) this.restoreFocus();
        }
    };

    private clearPending(): void {
        if (this.timeout !== null) this.global.clearTimeout(this.timeout);
        this.timeout = null;
    }

    private update(change: Partial<WidgetFullScreenState>): void {
        this.state = { ...this.state, ...change };
        for (const listener of this.listeners) listener(this.getState());
    }

    private captureEscape = (event: KeyboardEvent): void => {
        if (event.key !== 'Escape') return;
        const overlays = this.document.querySelectorAll('[role="dialog"][aria-modal="true"], dialog[open], #smartguide-permanent-map:not([hidden])');
        if (Array.from(overlays).some(overlay => !overlay.closest('[hidden], [aria-hidden="true"]') && this.global.getComputedStyle(overlay).display !== 'none')) {
            this.escapeWithOverlay.add(event);
        }
    };

    private escape = (event: KeyboardEvent): void => {
        if (event.key !== 'Escape' || event.defaultPrevented || this.escapeWithOverlay.has(event) || !this.state.fullScreen) return;
        event.preventDefault();
        this.requestFullScreen(false);
    };

    private restoreFocus(): void {
        const target = this.document.querySelector<HTMLElement>('[data-widget-full-screen-action]:not([hidden])')
            ?? this.document.querySelector<HTMLElement>('main, [role="main"]');
        if (!target) return;
        if (!target.hasAttribute('tabindex') && target.tagName !== 'BUTTON') target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
    }
}

export function getWidgetFullScreenBridge(global: Window, document: Document, options: WidgetFullScreenBridgeOptions): WidgetFullScreenBridge {
    const owner = global as BridgeWindow;
    return owner.__smartGuideWidgetFullScreenBridge ??= new WidgetFullScreenBridge(owner, document, options);
}
